import { useEffect, useState } from "react";
import qs from "qs";
import {
    Box,
    Typography,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import Layout from "../components/Layout";
import { API_BASE_URL } from "../config";
import axiosExt from "../axios";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { User } from "../types";

interface NpsPeriod {
    period: string;
    nps: number;
    responses: number;
}

interface NpsReport {
    globalNps: number;
    global: NpsPeriod[];
    users: {
        [userId: string]: NpsPeriod[];
    };
}

const ReportSurveyNpsPage = () => {
    const { t } = useTranslation();

    const [filterPeriod, setFilterPeriod] = useState<string>("month");
    const [filterFrom, setFilterFrom] = useState<string>(
        dayjs().startOf("year").format("YYYY-MM-DD")
    );
    const [filterTo, setFilterTo] = useState<string>(
        dayjs().endOf("year").format("YYYY-MM-DD")
    );

    const [users, setUsers] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const [report, setReport] = useState<NpsReport>();

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get(`${API_BASE_URL}/users/roles/admin-support`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const fetchNpsReport = async () => {
        try {
            const token = localStorage.getItem("token");
            const params = {
                fromDate: filterFrom,
                toDate: filterTo,
                period: filterPeriod,
            };

            const response = await axiosExt.get(`${API_BASE_URL}/satisfactionsurvey/nps`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
                paramsSerializer: {
                    serialize: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                },
            });

            setReport(response.data);
        } catch (error) {
            console.error("Error fetching NPS report:", error);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchNpsReport();
    }, []);

    // color palette for user lines
    const palette = [
        "#2e7d32",
        "#1565c0",
        "#d32f2f",
        "#f9a825",
        "#6a1b9a",
        "#00897b",
    ];

    const getUserColor = (idx: number) => {
        if (idx < palette.length) return palette[idx];
        const hue = (idx * 137.508) % 360;
        return `hsl(${hue}, 70%, 45%)`;
    };

    return (
        <Layout>
            <Box display="flex" width="100%" height="100vh- - 64px">
                {/* Left filters */}
                <Box width="20%" display="flex" flexDirection="column" pr={2}>
                    <Typography variant="h4" gutterBottom>
                        {t("Survey NPS Report")}
                    </Typography>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{t("Period")}</InputLabel>
                        <Select
                            value={filterPeriod}
                            onChange={(e) => setFilterPeriod(e.target.value)}
                        >
                            <MenuItem value="week">{t("Weekly")}</MenuItem>
                            <MenuItem value="month">{t("Monthly")}</MenuItem>
                            <MenuItem value="quarter">{t("Quarterly")}</MenuItem>
                            <MenuItem value="year">{t("Yearly")}</MenuItem>
                        </Select>
                    </FormControl>

                    {/* From / To */}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel shrink>{t("From")}</InputLabel>
                        <input
                            type="date"
                            value={filterFrom}
                            onChange={(e) => setFilterFrom(e.target.value)}
                            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel shrink>{t("To")}</InputLabel>
                        <input
                            type="date"
                            value={filterTo}
                            onChange={(e) => setFilterTo(e.target.value)}
                            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                        />
                    </FormControl>

                    {/* Multi select users */}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{t("Users")}</InputLabel>
                        <Select
                            multiple
                            value={selectedUsers}
                            onChange={(e) => setSelectedUsers(e.target.value as string[])}
                            renderValue={(selected) =>
                                users.filter((u) => selected.includes(u.id)).map((u) => u.userName).join(", ")
                            }
                        >
                            {users.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    <Checkbox checked={selectedUsers.includes(u.id)} />
                                    <ListItemText primary={u.userName} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ mt: 2, textAlign: "right" }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                fetchNpsReport();
                            }}
                        >
                            {t("Apply Filters")}
                        </Button>
                    </Box>
                </Box>

                {/* Right content */}
                <Box width="80%" display="flex" flexDirection="column" alignItems="stretch" p={2}>
                    {report && (
                        <>
                            <Typography variant="h6">
                                {t("Global NPS")}: {report.globalNps.toFixed(1)}
                            </Typography>
                            <Box flex={1} display="flex" flexDirection="column" justifyContent="flex-start" >
                            <LineChart
                                height={600}                // smaller height is fine
                                sx={{ mt: 0 }}
                                margin={{ top: 80, bottom: 40, left: 60, right: 20 }} // shrink outer space
                                grid={{ horizontal: true, vertical: false }}          // optional
                                series={[
                                    {
                                        data: report.global.map((p: NpsPeriod) => p.nps),
                                        label: "Global",
                                        color: "#000000",
                                    },
                                    ...selectedUsers.map((userId, idx) => {
                                        const user = users.find((u) => u.id === userId);
                                        return {
                                            data: (report.users[userId] || []).map((p: NpsPeriod) => p.nps),
                                            label: user ? user.userName : userId,
                                            color: getUserColor(idx),
                                        };
                                    }),
                                ]}
                                xAxis={[
                                    {
                                        data: report.global.map((p: NpsPeriod) => p.period),
                                        scaleType: "point",
                                    },
                                ]}
                                yAxis={[{ min: -100, max: 100 }]} // force scale so the plot fills space
                                />
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </Layout>
    );
};

export default ReportSurveyNpsPage;
